/**
 * ImpersonateDialog — reusable modal for superadmin "Sign in as" flow.
 *
 * Captures a MANDATORY audit-trail reason (min 6 chars) before calling
 * `POST /api/admin/users/{user_id}/impersonate` with `{ reason }`. On
 * success it calls onSuccess() so the parent can refresh auth + navigate.
 *
 * Used by both UsersPanel and SessionsPanel so the impersonation UX
 * (and the audit-log discipline) is identical everywhere.
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, ShieldAlert, UserCog } from "lucide-react";
import { toast } from "sonner";

const MIN_REASON = 6;

export default function ImpersonateDialog({
  open,
  onOpenChange,
  target,          // { user_id, name, email, role }
  onSuccess,       // async () => void — parent should refresh auth + navigate
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < MIN_REASON) {
      toast.error(`Please give a reason (at least ${MIN_REASON} characters) — this goes into the audit log.`);
      return;
    }
    try {
      setBusy(true);
      const { data } = await api.post(
        `/admin/users/${target.user_id}/impersonate`,
        { reason: trimmed }
      );
      toast.success(`Now acting as ${data.target_name}`);
      setReason("");
      onOpenChange(false);
      await onSuccess?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start impersonation");
    } finally {
      setBusy(false);
    }
  };

  if (!target) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent data-testid="impersonate-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5 text-purple-600" /> Sign in as {target.name}
          </DialogTitle>
          <DialogDescription>
            You'll act on {target.email} for up to 2 hours. Every action is written to the
            impersonation audit log along with the reason you enter below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
            Reason (required · audit log)
          </label>
          <Textarea
            data-testid="impersonate-reason-input"
            placeholder="e.g. Helping user recover a lost referral code — spoke to them via WhatsApp"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            disabled={busy}
            autoFocus
          />
          <div className="text-[11px] text-muted-foreground">
            Minimum {MIN_REASON} characters. Compliance disputes read this — be specific.
          </div>

          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 flex gap-2 text-xs">
            <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <span>
              Use only when the user cannot operate their account themselves. Your
              session and every subsequent action are attributed to you in the audit log,
              not the target user.
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="impersonate-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || reason.trim().length < MIN_REASON}
            data-testid="impersonate-submit"
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserCog className="h-4 w-4 mr-1" />}
            Sign in as user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
